import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FeaturedCreatorProductType } from '@/shared/types/marketplace.interface';
import { isArray, isEqual, lowerCase, get, isEmpty } from 'lodash';
import { formatProducts, groupById } from '@/shared/utils/marketplace';
import useBusinessSettingsDisplay from '@/lib/hooks/common/useBusinessSettingsDisplay';
import PopularProducts from '@/components/Shop/PopularProducts';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { Pagination, Select } from 'antd';
import Image from 'next/image';
import { PRICE_OPTIONS, SORT_OPTIONS } from '@/shared/constants/app';
import { useToggle } from 'react-use';
import { formatNumberWithCommas } from '@/shared/utils/utils';
import { useConfigProvider } from '@/lib/context';
import { FeaturedDesigners } from '@/components/Shop';
import { getCategoriesWithResults, searchJewelry, getAllJewelry } from '@/lib/service';
import FilterSidebar from '../FilterSidebar';
import { useRouter } from 'next/router';
import ProductItem from '@/components/Designers/Products/Item';
import ProductPlaceholder from '../../Common/ProductPlaceholder';
import queryString from 'query-string';
import { SEO_META } from '@/shared/seo/seo';
import { SEO_META_PAGES } from '@/shared/types/seo';
import Head from 'next/head';
import './index.less';

const paramValueToString = (param: any = {}) => {
  const obj: { [key: string]: string } = {};
  for (const k of Object.keys(param)) {
    obj[k] = String(param[k]);
  }
  return obj;
};

const convertToOptions = (designerList: any[]) => {
  const ret: { [key: string]: any } = {};
  if (!designerList) return ret;
  designerList.forEach(function (designer) {
    ret[designer.id] = {
      label: designer.title,
      value: designer.id,
    };
  });
  return ret;
};
const ShopAll = ({ param }: { param: { [key: string]: any } }) => {
  const { isMobile, screenSize } = useConfigProvider();
  const router = useRouter();

  const [tagsExpanded, tagsExpandedFun] = useToggle(true);
  const [queryParams, setQueryParams] = useState<{ [key: string]: any }>({
    ...param,
    sorts: param.sorts || '-globalSort',
    page: param.page || 1,
  });
  const [dataTotal, setDataTotal] = useState<number>(0);
  const [priceValue, setPriceValue] = useState<string[]>([]);
  const [designerValue, setDesignerValue] = useState<string[] | undefined>([]);
  const [categoryValue, setCategoryValue] = useState<string[]>([]);
  const [designerOptions, setDesignerOptions] = useState<{ [key: string]: any }>({});
  const [categoryOptions, setCategoryOptions] = useState<{ [key: string]: any }[]>([]);
  const [featuredCreatorProducts, setFeaturedCreatorProducts] = useState<
    FeaturedCreatorProductType[]
  >([]);
  const [mobileSearchType, setMobileSearchType] = useState<string>('');
  const [scrollToTop, setScrollToTop] = useState<boolean>(false);

  const { value: products } = useBusinessSettingsDisplay('FEATURED_PRODUCTS');
  const { value: pageCustomData } = useBusinessSettingsDisplay('SHOP_ALL_PAGE_DATA');
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [jewelryList, setJewelryList] = useState<any[]>([]);
  const [jewelryListMeta, setJewelryListMeta] = useState<{ [key: string]: any }>();

  const showSearchBox = (key: string) => {
    if (key === mobileSearchType) {
      setTimeout(() => {
        setMobileSearchType('');
      }, 500);
    } else {
      setMobileSearchType(key);
    }
  };

  const isSearchResultsPage = useMemo(() => {
    return (
      (param.text && param.text.length > 0) ||
      (param.predefinedText && param.predefinedText.length > 0)
    );
  }, [param.predefinedText, param.text]);

  const categoryDefaultValue = useCallback((): string[] => {
    if (!param.category) {
      return [];
    }

    if (isArray(param.category)) {
      return param.category;
    }
    return !param.category || param.category.length === 0 ? [] : param.category.split(',');
  }, [param.category]);

  const isRouteParamsChanged = useCallback(
    (query: { [key: string]: any }) => {
      return !isEqual(router.query, paramValueToString(query));
    },
    [router.query],
  );

  const paramToRoute = useCallback(
    (query: { [key: string]: any }) => {
      if (isRouteParamsChanged(query)) {
        router.replace(`/shop/all?${queryString.stringify(query)}`);
      }
    },
    [isRouteParamsChanged, router],
  );

  const scrollToTopOfPage = useCallback(() => {
    if (typeof window === 'object') {
      const node = document.querySelector('.product-grid');
      if (node) {
        node.scrollIntoView(true);
        window.scrollBy(0, -100);
      }
    }
  }, []);

  const queryData = useCallback(async (): Promise<any> => {
    try {
      const p = {
        ...param,
        page: param.page ? param.page - 1 : 0,
        size: isMobile ? 30 : 60,
      };
      return param.text || param.predefinedText ? await searchJewelry(p) : await getAllJewelry(p);
    } catch (error) {
      console.log('setShowGenericError', error);
    }
  }, [isMobile, param]);

  const getPageData = useCallback(async () => {
    if (scrollToTop) {
      scrollToTopOfPage();
      setScrollToTop(false);
    }
    setDataLoaded(false);
    setQueryParams((v) => ({
      ...v,
      isFetchStoreList: Object.keys(designerOptions).length === 0,
      isFetchPopularData: true,
    }));
    const res = await queryData();
    if (!res) return null;
    setDataLoaded(true);
    setJewelryList(res.list || []);
    setJewelryListMeta(res.meta || {});
    if (Object.keys(designerOptions).length === 0) {
      setDesignerOptions(convertToOptions(res.storeList));
    }
    setDataTotal(res.total);
  }, [designerOptions, queryData, scrollToTop, scrollToTopOfPage]);

  const applyFilters = useCallback(
    (data?: { type: string; value: Array<string> }) => {
      let thatParams: { [key: string]: any } = {};
      if (data?.type === 'text') {
        thatParams = {
          text: data?.value.length > 0 ? data?.value[0] : undefined,
        };
      }
      if (data?.type === 'price' && data?.value) {
        if (data?.value.length === 0) {
          delete queryParams['price'];
        }
        thatParams = {
          ...thatParams,
          minPriceRanges:
            data?.value.length > 0 ? '[' + (data?.value || []).join(',') + ']' : undefined,
        };
      }
      if (data?.type === 'designer') {
        if (data?.value.length === 0) {
          delete queryParams['designer'];
        }
        thatParams = {
          ...thatParams,
          storeId: data?.value.length > 0 ? (data?.value || []).join(',') : undefined,
        };
      }
      if (data?.type === 'category') {
        if (data?.value.length === 0) {
          delete queryParams['category'];
        }
        thatParams = {
          ...thatParams,
          category: data?.value.length > 0 ? (data?.value || []).join(',') : undefined,
        };
      }

      if (data?.type === 'clearFilters') {
        thatParams = {
          sorts: '-globalSort',
          page: 1,
        };
        setQueryParams({});
      } else {
        thatParams = {
          ...queryParams,
          ...thatParams,
          page: 1,
        };
      }
      if (mobileSearchType !== '') {
        setQueryParams(thatParams);
      } else {
        setScrollToTop(false);
        // hasUnappliedResults
        paramToRoute(thatParams);
      }
    },
    [mobileSearchType, paramToRoute, queryParams],
  );

  const createTags = useCallback((list: string[], dict: any, objectType: string) => {
    if (!list) {
      return;
    }

    return list.map((tag: string) => {
      return {
        text: get(dict, `${tag}.label`, tag),
        value: tag,
        type: objectType,
      };
    });
  }, []);

  const categoryOptionObj = useMemo(() => {
    return categoryOptions.reduce((acc, cur) => {
      if (lowerCase(cur.category) === 'other') return acc;
      return { ...acc, [cur.category]: { text: cur.category, value: cur.category } };
    }, {});
  }, [categoryOptions]);

  const allTags = useMemo(() => {
    const arr: { [key: string]: any }[] = [];
    arr.push.apply(arr, createTags(priceValue, PRICE_OPTIONS, 'price'));
    arr.push.apply(arr, createTags(designerValue, designerOptions, 'designer'));
    arr.push.apply(arr, createTags(categoryValue, categoryOptionObj, 'category'));
    if (!isEmpty(param.text)) {
      arr.push({
        text: param.text,
        value: param.text,
        type: 'text',
      });
    }
    if (isMobile) {
      if (arr.length > 0) {
        tagsExpandedFun(false);
      }
    } else if (arr.length > 12) {
      tagsExpandedFun(false);
    }
    return arr;
  }, [
    categoryOptionObj,
    categoryValue,
    createTags,
    designerOptions,
    designerValue,
    isMobile,
    param.text,
    priceValue,
    tagsExpandedFun,
  ]);

  const routeToParam = useCallback(() => {
    setPriceValue(
      !param.minPriceRanges || param.minPriceRanges.length === 0
        ? []
        : JSON.parse(param.minPriceRanges).map((range: string) => `[${range}]`),
    );
    setDesignerValue(
      param.storeId && param.storeId.length > 0
        ? param.storeId.split(',').map((id: string) => parseInt(id))
        : undefined,
    );
    setCategoryValue(categoryDefaultValue);
    setQueryParams((v) => ({ ...v, ...param }));
  }, [categoryDefaultValue, param]);

  const handleRemoveTag = useCallback(
    (tagValue: string, tagType: string) => {
      if (
        queryParams[tagType] ||
        (tagType === 'designer' && queryParams['storeId']) ||
        (tagType === 'price' && queryParams['minPriceRanges'])
      ) {
        switch (tagType) {
          case 'text':
            delete queryParams[tagType];
            break;
          case 'category':
            const categorys = queryParams[tagType].split(',').filter((x: string) => x !== tagValue);
            queryParams[tagType] = categorys.length > 0 ? categorys.join(',') : undefined;
            break;
          case 'designer':
            const storeIds = queryParams['storeId']
              .split(',')
              .filter((x: string) => x !== String(tagValue));
            queryParams['storeId'] = storeIds.length > 0 ? (storeIds || []).join(',') : undefined;
            break;
          case 'price':
            const prices = JSON.parse(queryParams['minPriceRanges'])
              .filter((x: number[]) => `[${x.toString()}]` !== tagValue)
              .map((x: number[]) => `[${x.toString()}]`);
            queryParams['minPriceRanges'] =
              prices.length > 0 ? `[${(prices || []).join(',')}]` : undefined;
            break;
        }
      }
      paramToRoute(queryParams);
    },
    [paramToRoute, queryParams],
  );

  const handleSortBy = useCallback(
    (value: string) => {
      setMobileSearchType('');
      paramToRoute({
        ...queryParams,
        sorts: value,
      });
    },
    [paramToRoute, queryParams],
  );
  useEffect(() => {
    getAllJewelry({
      jewelryIds: products?.value.map((product: FeaturedCreatorProductType) => product.productId),
      size: products?.value.length || 0,
    }).then((res) => {
      const productMap = groupById(res.list as any);
      const popularProducts = formatProducts(products?.value || [], productMap);
      setFeaturedCreatorProducts(popularProducts);
    });
    getCategoriesWithResults().then((res) => {
      setCategoryOptions(res.list || []);
    });
  }, [products]);

  useEffect(() => {
    routeToParam();
    getPageData();
  }, [param, routeToParam]);

  const handlePageChange = useCallback(
    (current: number) => {
      setJewelryList([]);
      setJewelryList([]);
      setScrollToTop(true);
      paramToRoute({ ...queryParams, page: current });
    },
    [paramToRoute, queryParams],
  );

  return (
    <div
      className="shop-all-page"
      style={{ overflow: mobileSearchType !== '' ? 'hidden' : 'auto' }}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              SEO_META[SEO_META_PAGES.MARKETPLACE_SHOP_ALL].jsonld({
                products: jewelryList || [],
              }),
            ),
          }}
        />
      </Head>
      <div className="data-body">
        <PopularProducts products={featuredCreatorProducts} numbered={false} vertical={false} />
        <div className="mobile-search-column">
          <div className="search-type utility-big" onClick={() => showSearchBox('FILTER')}>
            Filter By
          </div>
          {!isSearchResultsPage && (
            <div className="search-type utility-big" onClick={() => showSearchBox('SORT')}>
              Sort
            </div>
          )}
        </div>
        <div
          className={classNames('filter-overlay', {
            show: mobileSearchType === 'FILTER',
          })}
        >
          <div className="title">
            <span className="utility-big">Filter By</span>
            <CloseOutlined style={{ color: '#808080' }} onClick={() => setMobileSearchType('')} />
          </div>
          <div className="filter-body">
            <FilterSidebar
              designers={designerOptions}
              categoryOptions={categoryOptions}
              params={{ ...queryParams }}
              onChangeSearch={(type, value) => applyFilters({ type, value })}
            />
          </div>
        </div>
        <div
          className={classNames('filter-overlay', {
            show: mobileSearchType === 'SORT',
          })}
        >
          <div className="title">
            <span className="utility-big">Sort</span>
            <CloseOutlined style={{ color: '#808080' }} onClick={() => setMobileSearchType('')} />
          </div>
          <div className="filter-body">
            {SORT_OPTIONS.map((item, index) => (
              <div
                className="sort-option"
                key={`sort-option-item-${index}`}
                onClick={() => handleSortBy(item.value)}
              >
                <span
                  className={classNames('', {
                    active: queryParams?.sorts === item.value,
                  })}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          className={classNames('filter-apply utility-big', {
            show: mobileSearchType === 'FILTER',
          })}
          onClick={() => {
            setMobileSearchType('');
            setScrollToTop(false);
            // hasUnappliedResults
            paramToRoute(queryParams);
          }}
        >
          APPLY
        </div>

        <div id="product-grid" className="filters-and-data">
          <div className="search-column">
            <span
              className={classNames('num-products utility-small', {
                show: dataLoaded,
              })}
            >
              {formatNumberWithCommas(dataTotal)} products
            </span>
            <FilterSidebar
              designers={designerOptions}
              categoryOptions={categoryOptions}
              params={{ ...queryParams }}
              onChangeSearch={(type, value) => applyFilters({ type, value })}
            />
          </div>
          <div className="item-list-box">
            <div
              className={classNames('tags-expando', {
                expanded: tagsExpanded,
              })}
            >
              {(isMobile && allTags.length > 0) ||
                (!isMobile && allTags.length > 12 && (
                  <div className="title" onClick={tagsExpandedFun}>
                    <span className="utility-small">Applied Filters ({allTags.length})</span>
                    <Image
                      className="expand"
                      src="/images/carousel_next.png"
                      alt="Next Slide"
                      title="Next Slide"
                      width={20}
                      height={20}
                    />
                  </div>
                ))}
              {tagsExpanded && (
                <div className="tags-section">
                  {allTags.map((tag) => (
                    <div
                      className="tag"
                      key={tag.text}
                      onClick={() => handleRemoveTag(tag.value, tag.type)}
                    >
                      <span className="utility-small">{tag.text}</span>
                      <CloseOutlined style={{ color: '#000' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            {dataLoaded && isMobile && (
              <span className="num-products utility-small">
                {formatNumberWithCommas(dataTotal)} products
              </span>
            )}
            {dataLoaded && dataTotal === 0 ? (
              <span className="no-products-message utility-small">
                There are no products to show for these filters.
              </span>
            ) : (
              <div className="inner">
                {!isSearchResultsPage && (
                  <div className="sort-section">
                    <span className="sort-by-text body-text-small">Sort By</span>
                    <div className="select-item sort">
                      <Select
                        defaultValue={param.sorts}
                        options={SORT_OPTIONS}
                        placeholder="Popularity"
                        onChange={handleSortBy}
                      ></Select>
                    </div>
                  </div>
                )}
                <div className="grid">
                  {!dataLoaded
                    ? new Array(12).fill(1).map((_, index) => (
                        <div className="item" key={`product-item-${index}`}>
                          <ProductPlaceholder />
                        </div>
                      ))
                    : (jewelryList || []).map((item, index) => (
                        <ProductItem item={item} key={`product-item-${index}`} />
                      ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {jewelryList.length > 0 && (
          <div className="pagination">
            <Pagination
              showSizeChanger={false}
              total={dataTotal}
              defaultCurrent={queryParams?.page || 1}
              pageSize={isMobile || screenSize?.width <= 780 ? 30 : 60}
              onChange={(page) => {
                handlePageChange(page);
              }}
            />
          </div>
        )}
      </div>
      <div className="other-entrypoints">
        <FeaturedDesigners
          title="Best-Selling Creators"
          stores={pageCustomData?.value?.creators || []}
        />
      </div>
    </div>
  );
};

export default ShopAll;
